import { ArrowRight, Check, ChevronRight, Crown, ShieldAlert, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { EliteQualificationType, EliteStoreStatus, StoreHealthStatus, StoreHealthSummary } from '../hooks/api';
import { Button, buttonVariants } from '../shadcn/Button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '../shadcn/Dialog';
import { cn } from '../shadcn/utils';
import { format_elite_benefits } from '../utils/eliteStore';
import Logo from './Logo';

type StoreHealthCardProps = {
	summary?: StoreHealthSummary;
	loading: boolean;
	error: boolean;
	isMaster: boolean;
};

const HEALTH_COPY: Record<StoreHealthStatus, { label: string; tone: string }> = {
	GOOD: {
		label: 'Excelente',
		tone: 'text-emerald-700 dark:text-emerald-400'
	},
	NEUTRAL: {
		label: 'En observación',
		tone: 'text-amber-700 dark:text-amber-400'
	},
	BAD: {
		label: 'Necesita atención',
		tone: 'text-red-700 dark:text-red-400'
	},
	CRITICAL: {
		label: 'Necesita atención',
		tone: 'text-red-700 dark:text-red-400'
	},
	NO_DATA: { label: 'Sin información suficiente', tone: 'text-neutral-300' }
};

// Each customer level keeps the identity color the portal already uses for it
// (Azul #45C4D6, Plata #C0C0C0, Oro #D4AF37, Platino #E6E8E6), adjusted so the
// text stays readable on the cream and dark surfaces.
const LEVEL_STYLES: Record<string, { card: string; icon: string; detail: string; chip: string }> = {
	level_1: {
		card: 'border-sky-400/50 bg-sky-100/60 dark:border-sky-400/25 dark:bg-sky-400/[0.07]',
		icon: 'bg-sky-500 text-white dark:bg-sky-400 dark:text-sky-950',
		detail: 'text-sky-900 dark:text-sky-100/90',
		chip: 'bg-sky-500/20 text-sky-900 dark:bg-sky-400/15 dark:text-sky-50'
	},
	level_2: {
		card: 'border-slate-500/55 bg-slate-300/45 dark:border-slate-400/30 dark:bg-slate-500/[0.16]',
		icon: 'bg-slate-600 text-white dark:bg-slate-400 dark:text-slate-950',
		detail: 'text-slate-800 dark:text-slate-200/90',
		chip: 'bg-slate-600/20 text-slate-900 dark:bg-slate-400/15 dark:text-slate-50'
	},
	level_3: {
		card: 'border-amber-400/50 bg-amber-100/70 dark:border-amber-300/25 dark:bg-amber-300/[0.08]',
		icon: 'bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950',
		detail: 'text-amber-900 dark:text-amber-100/90',
		chip: 'bg-amber-500/20 text-amber-900 dark:bg-amber-300/15 dark:text-amber-50'
	},
	level_4: {
		card: 'border-zinc-400/70 bg-gradient-to-r from-white via-zinc-200/70 to-white dark:border-white/35 dark:from-white/[0.16] dark:via-white/[0.05] dark:to-white/[0.14]',
		icon: 'bg-zinc-800 text-white dark:bg-white dark:text-zinc-900',
		detail: 'text-zinc-800 dark:text-white/90',
		chip: 'bg-zinc-800/15 text-zinc-900 dark:bg-white/15 dark:text-white'
	},
	level_5: {
		card: 'border-neutral-600/50 bg-neutral-900/[0.06] dark:border-neutral-500/40 dark:bg-neutral-100/[0.05]',
		icon: 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-950',
		detail: 'text-neutral-100 dark:text-neutral-200',
		chip: 'bg-neutral-900/15 text-neutral-100 dark:bg-neutral-100/15 dark:text-neutral-50'
	}
};
const DEFAULT_LEVEL_STYLE = LEVEL_STYLES.level_3;

function getEliteCopy(status: EliteStoreStatus, branchRestricted: boolean, qualificationType?: EliteQualificationType | null) {
	const topSales = qualificationType === 'TOP_SALES';
	if (branchRestricted && (status === 'ELITE_STABLE' || status === 'ELITE_AT_RISK')) {
		return {
			label: topSales ? 'Élite Top Ventas · sucursal restringida' : 'Élite · sucursal restringida',
			title: 'Sucursal restringida',
			detail:
				'La tienda principal mantiene su categoría Élite, pero las condiciones especiales no están disponibles en esta sucursal.',
			dialogIconClassName: 'bg-red-500/15 text-red-700 dark:text-red-300',
			accentClassName: 'text-red-700 dark:text-red-300',
			frameClassName: 'bg-red-500/45',
			lightBeamColor: 'rgba(220, 38, 38, 1)',
			darkBeamColor: 'rgba(248, 113, 113, 0.95)',
			badgeClassName:
				'border-red-600/35 bg-red-500/[0.06] text-red-700 dark:border-red-400/35 dark:bg-red-400/[0.06] dark:text-red-300',
			Icon: ShieldAlert,
			pulsing: false
		};
	}
	if (status === 'ELITE_STABLE') {
		return {
			label: topSales ? 'Élite Top Ventas' : 'Élite',
			title: topSales ? 'Tienda Élite Top Ventas' : 'Tienda Élite',
			detail: topSales
				? 'Tu volumen de ventas y la salud de tu cartera activaron beneficios especiales.'
				: 'Tienes beneficios especiales activos.',
			dialogIconClassName:
				'bg-gradient-to-b from-amber-200 to-amber-400 text-amber-900 dark:from-amber-300 dark:to-amber-500 dark:text-amber-950',
			accentClassName: 'text-amber-700 dark:text-amber-300',
			frameClassName: 'bg-amber-500/45 dark:bg-amber-300/45',
			lightBeamColor: 'rgba(217, 119, 6, 1)',
			darkBeamColor: 'rgba(253, 224, 71, 0.98)',
			badgeClassName:
				'border-amber-600/35 bg-[#f4ecdd] text-amber-700 dark:border-amber-300/35 dark:bg-amber-300/[0.06] dark:text-amber-200',
			Icon: Crown,
			pulsing: false
		};
	}
	if (status === 'ELITE_AT_RISK') {
		return {
			label: 'Élite en riesgo',
			title: 'Élite en riesgo',
			detail: 'Tus beneficios siguen activos. Cuida la salud de tu cartera para mantenerlos.',
			dialogIconClassName:
				'bg-gradient-to-b from-amber-300 to-amber-500 text-amber-950 dark:from-amber-300 dark:to-amber-500',
			accentClassName: 'text-amber-700 dark:text-amber-300',
			frameClassName: 'bg-amber-500/45',
			lightBeamColor: 'rgba(217, 119, 6, 1)',
			darkBeamColor: 'rgba(251, 191, 36, 0.98)',
			badgeClassName:
				'border-amber-600/35 bg-[#f4ecdd] text-amber-700 dark:border-amber-400/35 dark:bg-amber-400/[0.06] dark:text-amber-300',
			Icon: ShieldAlert,
			pulsing: true
		};
	}
	if (status === 'NEAR_ELITE') {
		return {
			label: 'Cerca de ser Élite',
			title: 'Cerca de ser Élite',
			detail: 'Mantén una cartera saludable para entrar al grupo Élite y activar estos beneficios.',
			dialogIconClassName: 'bg-primary-500/15 text-primary-700 dark:text-primary-300',
			accentClassName: 'text-primary-700 dark:text-primary-300',
			frameClassName: 'bg-primary-500/45 dark:bg-primary-400/45',
			lightBeamColor: 'rgba(13, 148, 136, 1)',
			darkBeamColor: 'rgba(45, 212, 191, 0.95)',
			badgeClassName:
				'border-primary-700/30 bg-primary-500/[0.07] text-primary-800 dark:border-primary-300/35 dark:bg-primary-400/[0.06] dark:text-primary-300',
			Icon: Sparkles,
			pulsing: false
		};
	}
	return null;
}

// Metrics come from Firestore through unvalidated JSON: coerce before doing math or
// calling toFixed so a legacy string value degrades instead of crashing the dashboard.
function toFiniteNumber(value: unknown) {
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

function getDelta7d(summary?: StoreHealthSummary) {
	const current = toFiniteNumber(summary?.metrics?.delinquency_15d);
	const history = summary?.history || [];
	if (current === null || history.length < 2) return null;
	const latestDate = summary?.as_of_date;
	if (!latestDate) return null;
	const latestTimestamp = Date.parse(`${latestDate}T00:00:00Z`);
	const targetTimestamp = latestTimestamp - 7 * 24 * 60 * 60 * 1000;
	const comparison = history.find((point) => Date.parse(`${point.as_of_date}T00:00:00Z`) === targetTimestamp);
	const previous = toFiniteNumber(comparison?.metrics.delinquency_15d);
	return previous === null ? null : current - previous;
}

type ElitePresentation = NonNullable<ReturnType<typeof getEliteCopy>>;

function formatPercent(value?: number | null) {
	return value === null || value === undefined ? '—' : `${value.toFixed(2)}%`;
}

function EliteStatusBadge({ elite }: { elite: ElitePresentation }) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold',
				elite.badgeClassName
			)}
		>
			{elite.pulsing ? (
				<span className="size-2 rounded-full bg-current motion-safe:animate-[pulse_1.4s_ease-in-out_infinite]" />
			) : (
				<elite.Icon className="size-3" />
			)}
			{elite.label}
		</span>
	);
}

function EliteCardFrame({ children, elite }: { children: React.ReactNode; elite: ElitePresentation | null }) {
	return (
		<div
			className={cn(
				'relative isolate w-full overflow-hidden rounded-xl p-[2px] shadow-sm md:min-w-[360px] md:w-auto',
				elite?.frameClassName || 'bg-neutral-800'
			)}
		>
			{elite && (
				<>
					<span
						aria-hidden="true"
						className="pointer-events-none absolute -inset-[150%] mix-blend-multiply motion-safe:animate-[spin_4s_linear_infinite] motion-reduce:hidden dark:hidden"
						style={{
							background: `conic-gradient(from 0deg, transparent 0deg, transparent 290deg, ${elite.lightBeamColor} 330deg, transparent 360deg)`
						}}
					/>
					<span
						aria-hidden="true"
						className="pointer-events-none absolute -inset-[150%] hidden motion-safe:animate-[spin_4s_linear_infinite] dark:block motion-reduce:dark:hidden"
						style={{
							background: `conic-gradient(from 0deg, transparent 0deg, transparent 300deg, ${elite.darkBeamColor} 330deg, transparent 360deg)`
						}}
					/>
				</>
			)}
			<div className="relative z-10 rounded-[10px] bg-[#fffdf7] px-3.5 py-2.5 transition-colors duration-150 group-hover:bg-[#fffaf0] dark:bg-neutral-925 dark:group-hover:bg-neutral-900">
				{children}
			</div>
		</div>
	);
}

function EliteBenefitsDialog({
	summary,
	elite,
	benefits,
	isMaster
}: {
	summary?: StoreHealthSummary;
	elite: ElitePresentation;
	benefits: ReturnType<typeof format_elite_benefits>;
	isMaster: boolean;
}) {
	const canViewMetrics = isMaster && summary?.can_view_details === true;
	const benefitsActive = summary?.benefits_active === true;
	const rate = canViewMetrics ? toFiniteNumber(summary?.metrics?.delinquency_15d) : null;
	const salesCount = canViewMetrics ? toFiniteNumber(summary?.metrics?.sales_count_180d) : null;
	const topSales = summary?.elite_qualification_type === 'TOP_SALES';
	// The backend sends a bucketed standing, so every account can see the bar and
	// none of them can read a rank out of it.
	const positionPercent = summary?.elite_position_percent;
	const hasPosition = typeof positionPercent === 'number';
	// Keep the marker inside the track at both extremes.
	const markerLeft = hasPosition ? Math.min(96, Math.max(4, positionPercent)) : 0;
	const nearCutoff = summary?.elite_status === 'ELITE_AT_RISK';
	// The status comes from unvalidated JSON: fall back instead of crashing on an unknown value.
	const healthTone = (HEALTH_COPY[summary?.health_status || 'NO_DATA'] || HEALTH_COPY.NO_DATA).tone;
	const topPercentage = topSales ? summary?.elite_sales_top_percentage : summary?.elite_top_percentage;
	const topLabel =
		typeof topPercentage === 'number' && topPercentage > 0
			? topSales
				? `top ${Math.round(topPercentage)}% en ventas del país`
				: `top ${Math.round(topPercentage)}% del país`
			: 'grupo Élite';
	const delinquencyTopPercentage = summary?.elite_sales_delinquency_top_percentage;
	const delinquencyTopLabel =
		typeof delinquencyTopPercentage === 'number' && delinquencyTopPercentage > 0
			? `${Math.round(delinquencyTopPercentage)}% con mejor cartera del país`
			: 'grupo de tiendas con cartera saludable';
	const salesLookbackDays = summary?.elite_sales_lookback_days || 180;
	// A country can configure benefits for several levels (Azul, Plata, Oro…), so the
	// section title follows how many are actually resolved.
	const plural = benefits.length > 1;
	const benefitsTitle = benefitsActive
		? plural
			? 'Beneficios activos'
			: 'Beneficio activo'
		: summary?.branch_restricted
			? plural
				? 'Beneficios al recuperar la sucursal'
				: 'Beneficio al recuperar la sucursal'
			: plural
				? 'Beneficios al llegar a Élite'
				: 'Beneficio al llegar a Élite';

	// Child accounts (sucursal / vendedor) never see numbers: they get the program
	// rule and what they can do about it instead of metrics.
	let subtitle = '';
	if (summary?.branch_restricted) subtitle = '';
	else if (benefitsActive) subtitle = canViewMetrics ? `Estás en el ${topLabel}` : `Tu tienda principal está en el ${topLabel}`;
	else if (summary?.elite_status === 'NEAR_ELITE')
		subtitle = canViewMetrics ? `Cerca de entrar al ${topLabel}` : `Tu tienda principal está cerca del ${topLabel}`;

	let maintenanceLabel: string;
	let maintenanceText: string;
	if (topSales && summary?.branch_restricted) {
		maintenanceLabel = 'Tu reconocimiento se mantiene:';
		maintenanceText =
			'la tienda principal sigue siendo Élite Top Ventas, pero esta sucursal debe recuperar la salud de su cartera para usar los beneficios.';
	} else if (topSales) {
		maintenanceLabel = 'Cómo lo mantienes:';
		maintenanceText = `mantén tu volumen dentro del ${topLabel} y tu morosidad dentro del ${delinquencyTopLabel}.`;
	} else if (canViewMetrics) {
		if (!benefitsActive) {
			maintenanceLabel = 'Cómo llegas:';
			maintenanceText = `baja tu mora hasta entrar en el ${topLabel} y activa estos beneficios.`;
		} else if (nearCutoff) {
			maintenanceLabel = 'Atención:';
			maintenanceText = `estás cerca del límite del ${topLabel}. Cuida la morosidad de tu cartera para no perder tus beneficios.`;
		} else {
			maintenanceLabel = 'Vas muy bien:';
			maintenanceText =
				'eres una de las tiendas con mejor cartera del país. Mantén ese ritmo de pago y tus beneficios siguen contigo.';
		}
	} else if (nearCutoff) {
		maintenanceLabel = 'Atención:';
		maintenanceText =
			'la tienda está cerca de perder estos beneficios. Cuida la morosidad: ayuda a que tus clientes paguen a tiempo.';
	} else if (!benefitsActive && !summary?.branch_restricted) {
		maintenanceLabel = 'Cuida la morosidad:';
		maintenanceText = `la tienda entra al ${topLabel} cuando su mora baja. Ayuda a que tus clientes paguen a tiempo.`;
	} else {
		maintenanceLabel = 'Cuida la morosidad:';
		maintenanceText =
			'estos beneficios se mantienen mientras la mora de la tienda siga baja. Ayuda a que tus clientes paguen a tiempo.';
	}

	return (
		<DialogContent className="max-h-[calc(100vh-2rem)] max-w-md gap-5 overflow-y-auto rounded-2xl border-neutral-800 bg-[#fffdf7] p-5 dark:bg-neutral-950 sm:p-6">
			<DialogHeader className="gap-3 pr-8 text-left">
				<div className="flex items-center gap-3">
					<span className={cn('grid size-12 flex-none place-items-center rounded-2xl', elite.dialogIconClassName)}>
						<elite.Icon className="size-5" />
					</span>
					<div className="min-w-0">
						<DialogTitle className="text-xl font-bold text-neutral-100">{elite.title}</DialogTitle>
						{subtitle && <p className={cn('mt-0.5 text-sm font-semibold', elite.accentClassName)}>{subtitle}</p>}
					</div>
				</div>
				<DialogDescription className="text-left text-[15px] leading-6 text-neutral-400">{elite.detail}</DialogDescription>
			</DialogHeader>

			{canViewMetrics && topSales && salesCount !== null && (
				<div className="flex items-baseline gap-2.5">
					<p className="text-4xl font-bold tracking-tight text-amber-700 dark:text-amber-300">
						{salesCount.toLocaleString('es')}
					</p>
					<p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
						Ventas · {salesLookbackDays}d
					</p>
				</div>
			)}

			{canViewMetrics && !topSales && rate !== null && (
				<div className="flex items-baseline gap-2.5">
					<p className={cn('text-4xl font-bold tracking-tight', healthTone)}>{formatPercent(rate)}</p>
					<p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">Tu mora 15d</p>
				</div>
			)}

			{hasPosition && (
				<div className="border-t border-neutral-800 pt-4">
					<p className="text-sm font-bold text-neutral-100">{canViewMetrics ? 'Tu posición' : 'Posición de tu tienda'}</p>
					<div className="relative mt-3 h-2 rounded-full bg-gradient-to-r from-emerald-500 via-lime-400 to-amber-400">
						<span
							aria-hidden="true"
							className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#fffdf7] bg-neutral-100 shadow-sm dark:border-neutral-950"
							style={{ left: `${markerLeft}%` }}
						/>
					</div>
					<div className="mt-2 flex justify-between text-xs text-neutral-400">
						<span>{topSales ? 'Más ventas' : 'Mejores del país'}</span>
						<span>{topSales ? 'Límite Top Ventas' : 'Límite Élite'}</span>
					</div>
				</div>
			)}

			<div>
				<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">{benefitsTitle}</p>
				{benefits.length > 0 ? (
					<div className="mt-2.5 space-y-2">
						{benefits.map((benefit) => {
							const levelStyle = LEVEL_STYLES[benefit.level_key] || DEFAULT_LEVEL_STYLE;
							return (
								<div key={benefit.key} className={cn('flex items-start gap-3 rounded-xl border px-4 py-3', levelStyle.card)}>
									<span
										className={cn('mt-0.5 grid size-6 flex-none place-items-center rounded-full', levelStyle.icon)}
									>
										<Check className="size-3.5" />
									</span>
									<div className="min-w-0">
										<p className="text-sm font-bold text-neutral-100">Clientes {benefit.level}</p>
										<p className={cn('mt-0.5 text-sm', levelStyle.detail)}>{benefit.detail}</p>
										{benefit.advantage && (
											<p className={cn('mt-2 inline-block rounded-md px-2 py-1 text-xs font-bold', levelStyle.chip)}>
												{benefit.advantage}
											</p>
										)}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<p className="mt-2 text-sm text-neutral-400">No hay condiciones especiales configuradas para mostrar.</p>
				)}
			</div>

			{summary?.branch_restricted && (
				<div className="rounded-xl border border-red-500/25 bg-red-500/[0.06] px-4 py-3 text-sm text-neutral-300">
					<b className="text-red-700 dark:text-red-300">Sucursal restringida:</b> recupera la salud de esta sucursal para volver
					a usar las condiciones especiales.
				</div>
			)}

			<p className="text-sm leading-6 text-neutral-400">
				<b className="text-neutral-100">{maintenanceLabel}</b> {maintenanceText}
			</p>

			<DialogFooter className="flex-col gap-1 sm:flex-col">
				<DialogClose asChild>
					<Button variant="primary" className="w-full">
						Entendido
					</Button>
				</DialogClose>
				{isMaster && (
					<Link to="/salud" className={cn(buttonVariants({ variant: 'text' }), 'w-full')}>
						Ver detalle de mora <ArrowRight className="size-4" />
					</Link>
				)}
			</DialogFooter>
		</DialogContent>
	);
}

export default function StoreHealthCard({ summary, loading, error, isMaster }: StoreHealthCardProps) {
	// The status comes from unvalidated JSON: fall back instead of crashing on an unknown value.
	const health = HEALTH_COPY[summary?.health_status || 'NO_DATA'] || HEALTH_COPY.NO_DATA;
	const elite = getEliteCopy(
		summary?.elite_status || 'STANDARD',
		summary?.branch_restricted === true,
		summary?.elite_qualification_type
	);
	const exactRate = toFiniteNumber(summary?.metrics?.delinquency_15d);
	const delta7d = getDelta7d(summary);
	const benefits = format_elite_benefits(summary?.elite_benefits || {}, summary?.benefits_active === true);
	const hasEliteDetail = elite !== null;

	const card = (
		<EliteCardFrame elite={elite}>
			<div className="flex items-center justify-between gap-3">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-1.5">
						<span className="grid size-6 place-items-center rounded-lg bg-primary-500/10">
							<Logo size="min" className="size-3" />
						</span>
						<p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-neutral-400">Salud de la tienda</p>
						{elite && <EliteStatusBadge elite={elite} />}
					</div>
					<div className="mt-1 flex flex-wrap items-end gap-x-2.5 gap-y-0.5">
						<p className={cn('font-bold tracking-tight', health.tone, isMaster ? 'text-2xl' : 'text-lg')}>
							{loading
								? 'Cargando…'
								: error
									? 'No disponible'
									: isMaster && exactRate !== null
										? `${exactRate.toFixed(2)}%`
										: health.label}
						</p>
						{isMaster && delta7d !== null && (
							<span
								className={cn(
									'mb-0.5 text-[11px] font-semibold',
									delta7d > 0 ? 'text-amber-400' : delta7d < 0 ? 'text-emerald-400' : 'text-neutral-400'
								)}
							>
								{delta7d > 0 ? '+' : ''}
								{delta7d.toFixed(2)} pts · 7d
							</span>
						)}
					</div>
					<p className="mt-0.5 text-[11px] font-medium text-neutral-300">
						{elite?.detail || (isMaster ? health.label : 'Estado global de la tienda principal')}
					</p>
				</div>
				{(isMaster || hasEliteDetail) && <ChevronRight className="size-4 flex-none text-primary-400" />}
			</div>
		</EliteCardFrame>
	);

	if (!hasEliteDetail && isMaster) {
		return (
			<Link
				to="/salud"
				aria-label="Ver salud y mora 15d de la tienda"
				className="group block w-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 md:w-auto"
			>
				{card}
			</Link>
		);
	}
	if (!hasEliteDetail) return card;

	return (
		<Dialog>
			<DialogTrigger asChild>
				<button
					type="button"
					className="group w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 md:w-auto"
					aria-label={`Ver ${elite.label.toLowerCase()} y sus beneficios`}
				>
					{card}
				</button>
			</DialogTrigger>
			<EliteBenefitsDialog summary={summary} elite={elite} benefits={benefits} isMaster={isMaster} />
		</Dialog>
	);
}
